import { AudioBus } from "../engine/audio.ts";
import { Gfx } from "../engine/gfx.ts";
import { Input } from "../engine/input.ts";
import { hashSeed, readSeedFromUrl, Rng } from "../engine/rng.ts";
import { expToNext, mintMon } from "./mon.ts";
import { loadSave, newGame, normalizeSave, persist, PARTY_MAX, type SaveState } from "./save.ts";
import { dayKey, mergeDaily, mergeStats, prevDay, rollDay } from "./goals.ts";
import { applyGoal, checkBadges, type GoalKind } from "./goalEvents.ts";
import { goalsScene } from "../scenes/goals.ts";
import { Net, type NetHost } from "../net/client.ts";
import { battleScene } from "../scenes/battle.ts";
import {
  bagScene,
  clinicScene,
  indexScene,
  partyScene,
  pauseScene,
  pcScene,
  shopScene,
  summaryScene,
} from "../scenes/menus.ts";
import { overworldScene } from "../scenes/overworld.ts";
import { routePlanScene } from "../scenes/routePlan.ts";
import { routeMapScene } from "../scenes/routeMap.ts";
import { starterScene, titleScene } from "../scenes/title.ts";
import { tradeScene } from "../scenes/trade.ts";
import { nameScene, onlineScene } from "../scenes/online.ts";
import { marketScene } from "../scenes/market.ts";
import { raidScene } from "../scenes/raid.ts";
import { crownHouseScene } from "../scenes/crownHouse.ts";
import type { Ctx } from "./ctx.ts";
import type { Scene, SceneId, ScenePayload } from "./scenes.ts";
import { CIRCUIT_IDS, TRAINERS } from "../data/trainers.ts";
import { SPECIES_IDS, type SpeciesId } from "../data/species.ts";


function padCrew(party: ReturnType<typeof mintMon>[], rng: Rng, filler: SpeciesId = "moonling"): void {
  while (party.length < PARTY_MAX) party.push(mintMon(rng, filler, 5));
}

function readShot(): string | null {
  return new URLSearchParams(window.location.search).get("shot");
}

export class Game implements Ctx, NetHost {
  g: Gfx;
  input: Input;
  audio: AudioBus;
  rng: Rng;
  state: SaveState;
  reduced: boolean;
  now = 0;
  dt = 0;
  shot: string | null;
  grassQuiet = 0;
  net: Net;
  private stack: Scene[] = [];
  private last = 0;
  private duelShown = "";
  private tradeShown = "";

  constructor(canvas: HTMLCanvasElement) {
    this.g = new Gfx(canvas);
    this.input = new Input();
    this.audio = new AudioBus();
    const motion = window.matchMedia("(prefers-reduced-motion: reduce)");
    this.reduced = motion.matches;
    this.g.reduced = this.reduced;
    motion.addEventListener("change", () => {
      this.reduced = motion.matches;
      this.g.reduced = this.reduced;
    });
    this.shot = readShot();
    const seed = readSeedFromUrl();
    this.state = newGame(seed, "shibonk");
    this.rng = new Rng(this.state.rng);
    this.input.attach(document.getElementById("touch") ?? document.body);
    this.net = new Net(this);
    // A returning player joins the live world from the title so Continue loads the server copy.
    if (this.net.enabled && !this.shot && loadSave()) this.net.join(() => (this.inWorld() ? this.state : loadSave() ?? this.state));
    const unlock = () => this.audio.unlock();
    window.addEventListener("keydown", unlock);
    window.addEventListener("pointerdown", unlock);
    this.boot();
  }

  private make(id: SceneId): Scene {
    switch (id) {
      case "title":
        return titleScene(this);
      case "starter":
        return starterScene(this);
      case "overworld":
        return overworldScene(this);
      case "pause":
        return pauseScene(this);
      case "plan":
        return routePlanScene(this);
      case "map":
        return routeMapScene(this);
      case "battle":
        return battleScene(this);
      case "party":
        return partyScene(this);
      case "summary":
        return summaryScene(this);
      case "bag":
        return bagScene(this);
      case "shop":
        return shopScene(this);
      case "clinic":
        return clinicScene(this);
      case "pc":
        return pcScene(this);
      case "index":
        return indexScene(this);
      case "trade":
        return tradeScene(this);
      case "online":
        return onlineScene(this);
      case "name":
        return nameScene(this);
      case "market":
        return marketScene(this);
      case "raid":
        return raidScene(this);
      case "goals":
        return goalsScene(this);
      case "crownHouse":
        return crownHouseScene(this);
    }
  }

  private boot(): void {
    const shot = this.shot;
    if (!shot) {
      this.push("title");
      return;
    }
    const seed = this.state.seed;
    const rng = new Rng(hashSeed(`${seed}:shot`));
    if (shot === "title") {
      this.push("title");
      return;
    }
    if (shot === "continue") {
      if (!loadSave()) {
        const pocket = newGame("pond2", "shibonk", "Scout");
        pocket.money = 860;
        persist(pocket);
      }
      this.push("title");
      return;
    }
    if (shot === "starter") {
      this.push("starter");
      return;
    }
    this.state = newGame(seed, "shibonk", "Scout");
    this.state.money = 860;
    this.state.party.push(mintMon(rng, "whalord", 5));
    this.state.party.push(mintMon(rng, "rugfox", 4));
    this.state.party.push(mintMon(rng, "chopperdog", 6));
    this.state.seen = [...new Set([...this.state.seen, "laserpup", "whalord", "apeling", "chopperdog"])] as SaveState["seen"];
    this.state.caught = [...this.state.caught, "bogfiend", "wojakle", "chopperdog"];
    this.state.cosmetics.sticker = true;
    this.rng = rng;
    this.state.rng = rng.state;
    const fieldShots: Record<string, [number, number]> = {
      overworld: [15, 7], plaza: [9, 7], drippond: [26, 7], cliffs: [34, 9],
      chartwood: [13, 20], gulch: [26, 19], yard: [34, 23],
      frontier: [50, 8], frost: [64, 7], cache: [80, 7], dunes: [52, 19], bog: [66, 20], magma: [83, 20],
      harbor: [50, 36], caverns: [24, 36], grid: [78, 36], coral: [22, 46], cloud: [70, 46], gate: [50, 26],
    };
    if (fieldShots[shot]) {
      [this.state.x, this.state.y] = fieldShots[shot]!;
      this.state.dir = 2;
      this.push("overworld");
      return;
    }
    if (shot === "shop") {
      this.state.x = 13;
      this.state.y = 6;
      this.push("overworld");
      this.push("shop");
      return;
    }
    if (shot === "party") {
      this.push("overworld");
      this.push("party");
      return;
    }
    if (shot === "summary") {
      this.push("overworld");
      this.push("party");
      this.push("summary", { partyIndex: 1 });
      return;
    }
    if (shot === "clinic") {
      this.state.party[1]!.hp = Math.max(1, Math.floor(this.state.party[1]!.hp * 0.4));
      this.push("overworld");
      this.push("clinic");
      return;
    }
    if (shot === "pc") {
      this.state.pc.push(mintMon(rng, "moonling", 5));
      this.push("overworld");
      this.push("pc");
      return;
    }
    if (shot === "circuit" || shot === "circuit-final" || shot === "circuit-rematch") {
      const level = shot === "circuit" ? 23 : 28;
      const crews: Record<string, SpeciesId[]> = {
        standard: ["bogbaron", "shibonkx", "laserhound", "wojaked"],
        wetland: ["diamondshell", "slippeel", "yieldyeti", "bogbaron"],
        canopy: ["cloutking", "airdropqueen", "apemajor", "fomobeast"],
        night: ["copiumax", "candlewyrm", "rugwolf", "moonlord"],
      };
      const crew = new URLSearchParams(window.location.search).get("crew") ?? "standard";
      this.state.party = (Object.hasOwn(crews, crew) ? crews[crew] : crews.standard)!.map(id => mintMon(rng, id, level));
      this.state.seen = this.state.party.map(mon => mon.speciesId);
      this.state.caught = [...this.state.seen];
      this.state.defeatedTrainers = shot === "circuit" ? ["vell"] : shot === "circuit-final"
        ? ["vell", "rook", "mica"] : ["vell", ...CIRCUIT_IDS];
      const rival = shot === "circuit-final" ? TRAINERS.nix : TRAINERS.rook;
      this.state.x = rival.x;
      this.state.y = rival.y - 1;
      this.state.dir = 1;
      this.push("overworld");
      return;
    }
    if (shot === "mender") {
      this.state.party = [mintMon(rng, "shibonk", 9), mintMon(rng, "bogfiend", 9), mintMon(rng, "wojakle", 9)];
      this.state.seen = this.state.party.map(mon => mon.speciesId);
      this.state.caught = [...this.state.seen];
      this.state.defeatedTrainers = ["tavi"];
      this.state.x = TRAINERS.pella.x;
      this.state.y = TRAINERS.pella.y - 1;
      this.state.dir = 1;
      this.push("overworld");
      return;
    }
    if (shot === "courier") {
      this.state.party = [mintMon(rng, "shibonk", 15), mintMon(rng, "bogfiend", 15),
        mintMon(rng, "laserpup", 15), mintMon(rng, "hopiumite", 15)];
      this.state.seen = this.state.party.map(mon => mon.speciesId);
      this.state.caught = [...this.state.seen];
      this.state.x = TRAINERS.ilex.x;
      this.state.y = TRAINERS.ilex.y - 1;
      this.state.dir = 1;
      this.push("overworld");
      return;
    }
    if (shot === "boss" || shot === "kits") {
      this.state.party = [mintMon(rng, "bogbaron", 22), mintMon(rng, "shibonkx", 22),
        mintMon(rng, "laserhound", 22), mintMon(rng, "wojaked", 22)];
      this.state.seen = this.state.party.map((mon) => mon.speciesId);
      this.state.caught = [...this.state.seen];
      this.state.x = TRAINERS.vell.x;
      this.state.y = TRAINERS.vell.y - 1;
      this.state.dir = 1;
      this.push("overworld");
      if (shot === "kits") this.push("battle", { wild: mintMon(rng, "moonlord", 22) });
      return;
    }
    if (shot === "batch-a" || shot === "evolve-a") {
      this.state.party = [mintMon(rng, "cloutling", 15), mintMon(rng, "hopiumite", 14),
        mintMon(rng, "chartgob", 15), mintMon(rng, "bagsnail", 14)];
      padCrew(this.state.party, rng);
      this.state.seen = this.state.party.map((mon) => mon.speciesId);
      this.state.caught = [...this.state.seen];
      const wild = mintMon(rng, "diamondshell", 17);
      if (shot === "evolve-a") {
        this.state.party[0]!.exp = expToNext(15) - 1;
        wild.hp = 1;
      }
      this.push("overworld");
      this.push("battle", { wild });
      return;
    }
    if (shot === "batch-b" || shot === "evolve-b" || shot === "trainer") {
      this.state.party = [mintMon(rng, "fomoling", 18), mintMon(rng, "gasgob", 14),
        mintMon(rng, "slippeel", 14), mintMon(rng, "mintmoth", 17)];
      if (shot !== "trainer") padCrew(this.state.party, rng);
      this.state.seen = this.state.party.map((mon) => mon.speciesId);
      this.state.caught = [...this.state.seen];
      this.state.x = TRAINERS.sable.x;
      this.state.y = TRAINERS.sable.y - 1;
      this.state.dir = 1;
      this.push("overworld");
      if (shot === "trainer") return; // Approach, accept and play the actual route duel.
      const wild = mintMon(rng, "yieldyeti", 18);
      if (shot === "evolve-b") {
        this.state.party[0]!.exp = expToNext(18) - 1;
        wild.hp = 1;
      }
      this.push("battle", { wild });
      return;
    }
    if (shot === "craft-a") {
      this.state.party = [mintMon(rng, "yapling", 8), mintMon(rng, "ratiofin", 8),
        mintMon(rng, "finekit", 8), mintMon(rng, "lurkit", 10)];
      this.state.seen = [...SPECIES_IDS];
      this.state.caught = [...this.state.seen];
      this.push("overworld");
      this.push("battle", { wild: mintMon(rng, "scrollmite", 12) });
      return;
    }
    if (shot === "craft-c") {
      this.state.party = [mintMon(rng, "vibeling", 8), mintMon(rng, "draftkit", 9),
        mintMon(rng, "queueling", 8), mintMon(rng, "spoilpup", 10)];
      this.state.seen = [...SPECIES_IDS];
      this.state.caught = [...this.state.seen];
      this.push("overworld");
      this.push("battle", { wild: mintMon(rng, "clipfinch", 12) });
      return;
    }
    if (shot === "craft-d") {
      this.state.party = [mintMon(rng, "toquelin", 8), mintMon(rng, "poutkit", 9),
        mintMon(rng, "ghostkit", 8), mintMon(rng, "crashpup", 10)];
      this.state.seen = [...SPECIES_IDS];
      this.state.caught = [...this.state.seen];
      this.push("overworld");
      this.push("battle", { wild: mintMon(rng, "pingling", 12) });
      return;
    }
    if (shot === "craft-e") {
      this.state.party = [mintMon(rng, "slayling", 8), mintMon(rng, "cookling", 9),
        mintMon(rng, "midling", 8), mintMon(rng, "lockkit", 10)];
      this.state.seen = [...SPECIES_IDS];
      this.state.caught = [...this.state.seen];
      this.push("overworld");
      this.push("battle", { wild: mintMon(rng, "depkit", 12) });
      return;
    }
    if (shot === "craft-f") {
      this.state.party = [mintMon(rng, "ehcorn", 8), mintMon(rng, "snoozlet", 9),
        mintMon(rng, "coneling", 10), mintMon(rng, "syrupup", 9)];
      padCrew(this.state.party, rng);
      this.state.seen = [...SPECIES_IDS];
      this.state.caught = [...this.state.seen];
      this.push("overworld");
      this.push("battle", { wild: mintMon(rng, "bufferret", 12) });
      return;
    }
    if (shot === "craft-g") {
      this.state.party = [mintMon(rng, "honklet", 12), mintMon(rng, "chillip", 9),
        mintMon(rng, "crumbit", 9), mintMon(rng, "puckup", 10)];
      padCrew(this.state.party, rng);
      this.state.seen = [...SPECIES_IDS];
      this.state.caught = [...this.state.seen];
      this.push("overworld");
      this.push("battle", { wild: mintMon(rng, "tabbit", 12) });
      return;
    }
    if (shot === "frontier-battle" || shot === "frontier-crystal" || shot === "frontier-bog") {
      // Frontier review fixtures: a Lv30 frontier crew facing the local headliner.
      const spot = { "frontier-battle": [83, 20], "frontier-crystal": [80, 7], "frontier-bog": [66, 20] } as const;
      [this.state.x, this.state.y] = spot[shot];
      this.state.party = [mintMon(rng, "wickwolf", 31), mintMon(rng, "coldvault", 32), mintMon(rng, "prismhound", 30), mintMon(rng, "honeybaron", 32)];
      this.state.seen = [...new Set([...this.state.seen, ...this.state.party.map((m) => m.speciesId)])] as SaveState["seen"];
      const foe = shot === "frontier-battle" ? "infernomint" : shot === "frontier-crystal" ? "shardking" : "rugwraith";
      this.push("overworld");
      this.push("battle", { wild: mintMon(rng, foe, 34) });
      return;
    }
    if (shot === "daily" || shot === "goals" || shot === "shiny") {
      // Engagement fixtures: a streak in progress, some mission progress and a few badges.
      this.state.party = [mintMon(rng, "lunawolf", 36), mintMon(rng, "fanstorm", 35), mintMon(rng, "krillionaire", 34)];
      this.state.caught = [...new Set([...this.state.caught, ...SPECIES_IDS.slice(0, 27)])] as SaveState["caught"];
      this.state.seen = [...new Set([...this.state.seen, ...this.state.caught])] as SaveState["seen"];
      const today = this.today();
      this.state.daily = { day: prevDay(today), missions: [], streak: 2, streakClaimed: true, bonusClaimed: false };
      if (shot === "shiny") {
        [this.state.x, this.state.y] = [70, 46];
        this.push("overworld");
        const wild = mintMon(rng, "lunapup", 36);
        wild.shiny = true;
        this.push("battle", { wild });
        return;
      }
      [this.state.x, this.state.y] = [50, 36];
      this.push("overworld");
      if (shot === "goals") {
        this.state.stats.caught += 2;
        this.state.stats.wildWins += 3;
        this.state.stats.superHits += 5;
        this.state.stats.steps += 400;
        this.state.stats.rests += 1;
        this.state.daily!.streakClaimed = true;
        checkBadges(this.state);
        this.state.title = "Collector";
        this.push("goals");
      }
      return;
    }
    if (shot === "sidechain-harvest" || shot === "sidechain-ghost" || shot === "sidechain-jungle" || shot === "sidechain-sakura" || shot === "sidechain-ruins") {
      // Sidechain review fixtures: a Lv46 crew facing each zone's signature wild.
      const spot = { "sidechain-harvest": [122, 46], "sidechain-ghost": [122, 36], "sidechain-jungle": [112, 21], "sidechain-sakura": [106, 7], "sidechain-ruins": [128, 7] } as const;
      const foe = { "sidechain-harvest": "gourdian", "sidechain-ghost": "rektoplasm", "sidechain-jungle": "beakfolio", "sidechain-sakura": "ryukoin", "sidechain-ruins": "sphynxfi" } as const;
      [this.state.x, this.state.y] = spot[shot];
      this.state.party = [mintMon(rng, "arborealm", 46), mintMon(rng, "crowllateral", 46), mintMon(rng, "colosstake", 45), mintMon(rng, "koinfish", 44)];
      this.state.seen = [...new Set([...this.state.seen, ...this.state.party.map((m) => m.speciesId)])] as SaveState["seen"];
      this.push("overworld");
      this.push("battle", { wild: mintMon(rng, foe[shot], 45) });
      return;
    }
    if (shot === "undernet-grid" || shot === "undernet-cloud" || shot === "undernet-reef" || shot === "undernet-cave") {
      // Undernet review fixtures: a Lv38 crew whose lead opens with a condition move.
      const spot = { "undernet-grid": [78, 36], "undernet-cloud": [70, 46], "undernet-reef": [22, 46], "undernet-cave": [24, 36] } as const;
      [this.state.x, this.state.y] = spot[shot];
      const lead = mintMon(rng, shot === "undernet-reef" ? "medusell" : shot === "undernet-cave" ? "shroomancer" : "fanstorm", 38);
      const opener = shot === "undernet-reef" ? "spreadFud" : shot === "undernet-cave" ? "glowSpores" : "gasWar";
      lead.moves = [{ id: opener, pp: 15 }, ...lead.moves.filter((m) => m.id !== opener).slice(0, 3)];
      this.state.party = [lead, mintMon(rng, "rocketitan", 38), mintMon(rng, "asicarab", 37), mintMon(rng, "krillionaire", 36)];
      this.state.seen = [...new Set([...this.state.seen, ...this.state.party.map((m) => m.speciesId)])] as SaveState["seen"];
      const foe = shot === "undernet-grid" ? "chatlord" : shot === "undernet-cloud" ? "lunawolf" : shot === "undernet-reef" ? "crabstack" : "stuckwing";
      this.push("overworld");
      this.push("battle", { wild: mintMon(rng, foe, 37) });
      return;
    }
    if (shot === "battle" || shot === "catch" || shot === "moves" || shot === "evolve") {
      const wild =
        shot === "catch"
          ? mintMon(rng, "moonling", 6)
          : shot === "moves"
            ? mintMon(rng, "rugfox", 6)
            : mintMon(rng, "gigachomp", 6);
      if (shot === "evolve") {
        const lead = mintMon(rng, "shibonk", 15);
        lead.exp = expToNext(15) - 1;
        this.state.party[0] = lead;
        wild.hp = 1;
      }
      if (shot === "catch") wild.hp = Math.max(1, Math.floor(wild.hp * 0.25));
      this.push("overworld");
      this.push("battle", { wild });
      return;
    }
    this.push("title");
  }

  push(id: SceneId, payload?: ScenePayload): void {
    this.input.reset();
    const scene = this.make(id);
    this.stack.push(scene);
    scene.enter(payload);
  }

  replace(id: SceneId, payload?: ScenePayload): void {
    const top = this.stack.pop();
    top?.exit();
    if (id === "title") {
      while (this.stack.length) this.stack.pop()!.exit();
    }
    this.push(id, payload);
  }

  pop(): void {
    this.input.reset();
    const top = this.stack.pop();
    top?.exit();
    if (!this.stack.length) this.push("title");
  }

  save(): void {
    this.state.rng = this.rng.state;
    persist(this.state);
    this.net.queueSave(this.state);
  }

  /** Staged screenshots and fixtures never earn missions or badges (except the goals fixtures). */
  private goalsLive(): boolean {
    return !this.shot || this.shot === "daily" || this.shot === "goals";
  }

  goal(kind: GoalKind, n = 1, meta: { species?: string; shiny?: boolean; zone?: string } = {}): void {
    if (!this.goalsLive()) return;
    for (const line of applyGoal(this.state, kind, n, meta)) this.toast(line);
  }

  /** Queue a line for the street's toast (works offline too). */
  toast(text: string): void {
    this.net.notices.push(text);
  }

  today(): string {
    const q = typeof window !== "undefined" ? new URLSearchParams(window.location?.search ?? "").get("day") : null;
    return q && /^\d{4}-\d{2}-\d{2}$/.test(q) ? q : dayKey(Date.now());
  }

  rollDaily(): boolean {
    if (!this.goalsLive()) return false;
    const fresh = rollDay(this.state, this.today());
    if (fresh) this.save();
    return fresh;
  }

  /** Live duels and trades open over the street, closing any menu the player had open. */
  private routeNet(): void {
    const net = this.net;
    const toStreet = () => {
      while (this.stack.length > 1 && this.stack[this.stack.length - 1]!.id !== "overworld") this.pop();
    };
    if (net.duel && net.duel.id !== this.duelShown && this.inWorld()) {
      this.duelShown = net.duel.id;
      // A raid fight opens over the Raid Gate, so a wiped crew is one press away from rejoining.
      if (!(net.duel.raid && this.stack.at(-1)?.id === "raid")) toStreet();
      this.push("battle", { duel: net.duel });
    }
    if (net.trade && net.trade.id !== this.tradeShown && this.inWorld()) {
      this.tradeShown = net.trade.id;
      toStreet();
      this.push("trade");
    }
  }

  /** True once a save is being played (the street is somewhere on the stack). */
  inWorld(): boolean {
    return this.stack.some((s) => s.id === "overworld");
  }

  /** Online: the server's copy replaces ours (trade, duel prize, another device). Position stays local. */
  adoptSave(raw: SaveState, why: string): void {
    const next = normalizeSave(raw);
    if (!next) return;
    if (why === "restore") {
      // A restored account is a different scout: nothing of this device's save carries over.
      this.state = next;
      this.rng = new Rng(next.rng);
      persist(next);
      return;
    }
    if (this.inWorld()) {
      next.x = this.state.x;
      next.y = this.state.y;
      next.dir = this.state.dir;
      next.rng = this.rng.state;
      next.playMs = Math.max(next.playMs, this.state.playMs);
      // Progress only grows: keep the larger of each counter, both copies' claims and badges.
      next.stats = mergeStats(this.state.stats, next.stats);
      next.daily = mergeDaily(this.state.daily, next.daily);
      next.badges = [...new Set([...this.state.badges, ...next.badges])];
      next.title = this.state.title;
      // Clues and encore wins only grow; the clock follows this device.
      next.clues = [...new Set([...this.state.clues, ...next.clues])];
      for (const [id, week] of Object.entries(this.state.encore) as [keyof SaveState["encore"], number][]) {
        if (next.defeatedTrainers.includes(id)) next.encore[id] = Math.max(week, next.encore[id] ?? -1);
      }
      next.clockShift = this.state.clockShift;
      this.state = next;
      for (const line of checkBadges(next)) this.toast(line);
    }
    persist(next);
  }

  persistRng(): void {
    this.state.rng = this.rng.state;
  }

  /** Draw the stack from the top-most opaque scene upward (overlays draw over their base). */
  drawStack(): void {
    const g = this.g;
    g.shakeX = g.shakeY = 0;
    let base = this.stack.length - 1;
    while (base > 0 && this.stack[base]!.overlay) base--;
    for (let i = Math.max(0, base); i < this.stack.length; i++) {
      g.beginFrame();
      this.stack[i]!.draw();
    }
    g.beginFrame();
  }

  start(): void {
    (window as unknown as { memecraft?: Game }).memecraft = this;
    this.last = performance.now();
    const loop = (t: number) => {
      this.dt = Math.min(40, t - this.last);
      this.last = t;
      this.now = t;
      this.state.playMs += this.dt;
      this.input.poll?.(t);
      this.net.tick(this.dt);
      this.routeNet();
      this.stack[this.stack.length - 1]?.update();
      this.drawStack();
      this.g.present();
      this.input.endFrame();
      requestAnimationFrame(loop);
    };
    requestAnimationFrame(loop);
  }
}
